import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, Power, PlayCircle, Loader2, AlertCircle, CheckCircle2, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface AlertRule {
    id: number;
    name: string;
    description?: string;
    enabled: boolean;
    priority: number;
    // Price criteria
    minDiscountVsMarketValue?: number;
    minDiscountVsWineSearcher?: number;
    minDiscountVsLastTransaction?: number;
    maxPriceAbsolute?: number;
    minPriceAbsolute?: number;
    // Quality criteria
    minCriticScore?: number;
    preferredCritics?: string;
    requireCriticReview: boolean;
    // Drinking window
    drinkingWindowMinYear?: number;
    drinkingWindowMaxYear?: number;
    acceptedMaturities?: string;
    // Wine characteristics
    regions?: string;
    producers?: string;
    nameContains?: string;
    classifications?: string;
    minVintage?: number;
    maxVintage?: number;
    // Bidding
    biddingOnly: boolean;
    maxBidToValueRatio?: number;
    noBidsOnly: boolean;
    // Triggers
    alertOnNewListings: boolean;
    alertOnPriceDrop: boolean;
    alertOnRevaluation: boolean;
    minPriceDropPct?: number;
    // Metadata
    createdAt: string;
    updatedAt: string;
    lastTriggered?: string;
    triggerCount: number;
}

const AlertRules: React.FC = () => {
    const [rules, setRules] = useState<AlertRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
    const [testingRuleId, setTestingRuleId] = useState<number | null>(null);
    const [testResult, setTestResult] = useState<any | null>(null);

    // Form state
    const [formData, setFormData] = useState<Partial<AlertRule>>({
        name: '',
        description: '',
        enabled: true,
        priority: 100,
        requireCriticReview: false,
        biddingOnly: false,
        noBidsOnly: false,
        alertOnNewListings: true,
        alertOnPriceDrop: true,
        alertOnRevaluation: true,
    });

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/rules`);
            if (!response.ok) throw new Error('Failed to fetch rules');
            const data = await response.json();
            setRules(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            const url = editingRule
                ? `${API_BASE_URL}/api/alerts/rules/${editingRule.id}`
                : `${API_BASE_URL}/api/alerts/rules`;
            const method = editingRule ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error('Failed to save rule');

            await fetchRules();
            handleCloseForm();
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete this alert rule?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/rules/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete rule');
            await fetchRules();
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleToggleEnabled = async (rule: AlertRule) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/rules/${rule.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...rule, enabled: !rule.enabled }),
            });
            if (!response.ok) throw new Error('Failed to update rule');
            await fetchRules();
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleTest = async (ruleId: number) => {
        setTestingRuleId(ruleId);
        setTestResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/rules/${ruleId}/test`, {
                method: 'POST',
            });
            if (!response.ok) throw new Error('Failed to test rule');
            const data = await response.json();
            setTestResult(data);
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        } finally {
            setTestingRuleId(null);
        }
    };

    const handleEdit = (rule: AlertRule) => {
        setEditingRule(rule);
        setFormData(rule);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingRule(null);
        setFormData({
            name: '',
            description: '',
            enabled: true,
            priority: 100,
            requireCriticReview: false,
            biddingOnly: false,
            noBidsOnly: false,
            alertOnNewListings: true,
            alertOnPriceDrop: true,
            alertOnRevaluation: true,
        });
    };

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--ink-900)',
        },
        button: {
            padding: '10px 20px',
            backgroundColor: 'var(--claret-600)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: '500',
        },
        ruleCard: (enabled: boolean) => ({
            backgroundColor: 'white',
            border: `1px solid ${enabled ? 'var(--border)' : 'var(--danger-border)'}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            opacity: enabled ? 1 : 0.6,
        }),
        ruleHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px',
        },
        ruleName: {
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--ink-900)',
            marginBottom: '4px',
        },
        ruleDescription: {
            fontSize: '14px',
            color: 'var(--ink-500)',
            marginBottom: '12px',
        },
        ruleStats: {
            display: 'flex',
            gap: '16px',
            fontSize: '13px',
            color: 'var(--ink-500)',
            marginTop: '12px',
        },
        actions: {
            display: 'flex',
            gap: '8px',
        },
        iconButton: {
            padding: '8px',
            backgroundColor: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
        },
        modal: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modalContent: {
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            width: '100%',
        },
        formGroup: {
            marginBottom: '16px',
        },
        label: {
            display: 'block',
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--ink-700)',
            marginBottom: '4px',
        },
        input: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
        },
        textarea: {
            width: '100%',
            padding: '8px 12px',
            border: '1px solid var(--border-strong)',
            borderRadius: '6px',
            fontSize: '14px',
            minHeight: '80px',
            resize: 'vertical' as const,
        },
        checkbox: {
            marginRight: '8px',
        },
        section: {
            marginBottom: '24px',
            paddingBottom: '24px',
            borderBottom: '1px solid var(--border)',
        },
        sectionTitle: {
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--ink-900)',
            marginBottom: '12px',
        },
        row: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
        },
        badge: (enabled: boolean) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            backgroundColor: enabled ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: enabled ? 'var(--success)' : 'var(--danger)',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
        }),
    };

    if (loading) {
        return (
            <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
                <Loader2 size={32} className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
                <div style={{textAlign: 'center'}}>
                    <AlertCircle size={48} color="var(--danger)" />
                    <p style={{marginTop: '16px', color: 'var(--danger)'}}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Alert Rules</h1>
                <button style={styles.button} onClick={() => setShowForm(true)}>
                    <Plus size={18} />
                    New Rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div style={{textAlign: 'center', padding: '48px', color: 'var(--ink-500)'}}>
                    <p>No alert rules yet. Create one to get started!</p>
                </div>
            ) : (
                rules.map((rule) => (
                    <div key={rule.id} style={styles.ruleCard(rule.enabled)}>
                        <div style={styles.ruleHeader}>
                            <div style={{flex: 1}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    <h3 style={styles.ruleName}>{rule.name}</h3>
                                    <span style={styles.badge(rule.enabled)}>
                                        {rule.enabled ? <><CheckCircle2 size={14} /> Enabled</> : <>Disabled</>}
                                    </span>
                                    <span style={{...styles.badge(true), backgroundColor: 'var(--info-bg)', color: 'var(--info)'}}>
                                        Priority: {rule.priority}
                                    </span>
                                </div>
                                {rule.description && <p style={styles.ruleDescription}>{rule.description}</p>}

                                {/* Show some key criteria */}
                                <div style={{marginTop: '12px', fontSize: '13px', color: 'var(--ink-600)'}}>
                                    {rule.minDiscountVsMarketValue && (
                                        <div>• Min discount vs market: {(rule.minDiscountVsMarketValue * 100).toFixed(0)}%</div>
                                    )}
                                    {rule.minCriticScore && (
                                        <div>• Min critic score: {rule.minCriticScore}+</div>
                                    )}
                                    {rule.regions && (
                                        <div>• Regions: {rule.regions}</div>
                                    )}
                                </div>

                                <div style={styles.ruleStats}>
                                    <span>Triggered: {rule.triggerCount} times</span>
                                    {rule.lastTriggered && (
                                        <span>Last: {new Date(rule.lastTriggered).toLocaleString()}</span>
                                    )}
                                </div>
                            </div>

                            <div style={styles.actions}>
                                <button
                                    style={styles.iconButton}
                                    onClick={() => handleToggleEnabled(rule)}
                                    title={rule.enabled ? 'Disable' : 'Enable'}
                                >
                                    <Power size={18} color={rule.enabled ? 'var(--success)' : 'var(--ink-500)'} />
                                </button>
                                <button
                                    style={styles.iconButton}
                                    onClick={() => handleTest(rule.id)}
                                    title="Test rule"
                                    disabled={testingRuleId === rule.id}
                                >
                                    {testingRuleId === rule.id ? (
                                        <Loader2 size={18} className="spinner" />
                                    ) : (
                                        <PlayCircle size={18} color="var(--claret-600)" />
                                    )}
                                </button>
                                <button
                                    style={styles.iconButton}
                                    onClick={() => handleEdit(rule)}
                                    title="Edit"
                                >
                                    <Edit2 size={18} color="var(--ink-500)" />
                                </button>
                                <button
                                    style={styles.iconButton}
                                    onClick={() => handleDelete(rule.id)}
                                    title="Delete"
                                >
                                    <Trash2 size={18} color="var(--danger)" />
                                </button>
                            </div>
                        </div>

                        {testResult && testResult.ruleId === rule.id && (
                            <div style={{marginTop: '12px', padding: '12px', backgroundColor: 'var(--border)', borderRadius: '6px'}}>
                                <strong>Test Result:</strong> Found {testResult.matchCount} matching wines
                            </div>
                        )}
                    </div>
                ))
            )}

            {/* Form Modal */}
            {showForm && (
                <div style={styles.modal} onClick={handleCloseForm}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
                            <h2 style={{fontSize: '20px', fontWeight: '600'}}>
                                {editingRule ? 'Edit Rule' : 'New Alert Rule'}
                            </h2>
                            <button style={{...styles.iconButton, border: 'none'}} onClick={handleCloseForm}>
                                <X size={24} />
                            </button>
                        </div>

                        {/* Basic Info */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>Basic Information</h3>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Name *</label>
                                <input
                                    style={styles.input}
                                    value={formData.name || ''}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="e.g., Deep Discount Burgundy"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Description</label>
                                <textarea
                                    style={styles.textarea}
                                    value={formData.description || ''}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    placeholder="Describe what this rule does..."
                                />
                            </div>
                            <div style={styles.row}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Priority (1 = highest)</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.priority || 100}
                                        onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>
                                        <input
                                            type="checkbox"
                                            style={styles.checkbox}
                                            checked={formData.enabled || false}
                                            onChange={(e) => setFormData({...formData, enabled: e.target.checked})}
                                        />
                                        Enabled
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Price Criteria */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>Price Criteria</h3>
                            <div style={styles.row}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Min Discount vs Market (%)</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.minDiscountVsMarketValue ? formData.minDiscountVsMarketValue * 100 : ''}
                                        onChange={(e) => setFormData({...formData, minDiscountVsMarketValue: e.target.value ? parseFloat(e.target.value) / 100 : undefined})}
                                        placeholder="e.g., 20 for 20%"
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Min Discount vs Wine Searcher (%)</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.minDiscountVsWineSearcher ? formData.minDiscountVsWineSearcher * 100 : ''}
                                        onChange={(e) => setFormData({...formData, minDiscountVsWineSearcher: e.target.value ? parseFloat(e.target.value) / 100 : undefined})}
                                        placeholder="e.g., 20 for 20%"
                                    />
                                </div>
                            </div>
                            <div style={styles.row}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Max Price (£)</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.maxPriceAbsolute || ''}
                                        onChange={(e) => setFormData({...formData, maxPriceAbsolute: e.target.value ? parseFloat(e.target.value) : undefined})}
                                        placeholder="e.g., 500"
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Min Price (£)</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.minPriceAbsolute || ''}
                                        onChange={(e) => setFormData({...formData, minPriceAbsolute: e.target.value ? parseFloat(e.target.value) : undefined})}
                                        placeholder="e.g., 50"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Quality Criteria */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>Quality Criteria</h3>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Min Critic Score</label>
                                <input
                                    type="number"
                                    style={styles.input}
                                    value={formData.minCriticScore || ''}
                                    onChange={(e) => setFormData({...formData, minCriticScore: e.target.value ? parseInt(e.target.value) : undefined})}
                                    placeholder="e.g., 92"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={formData.requireCriticReview || false}
                                        onChange={(e) => setFormData({...formData, requireCriticReview: e.target.checked})}
                                    />
                                    Require at least one critic review
                                </label>
                            </div>
                        </div>

                        {/* Wine Characteristics */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>Wine Characteristics</h3>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Regions (comma-separated)</label>
                                <input
                                    style={styles.input}
                                    value={formData.regions || ''}
                                    onChange={(e) => setFormData({...formData, regions: e.target.value})}
                                    placeholder="e.g., Burgundy, Bordeaux"
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Name Contains</label>
                                <input
                                    style={styles.input}
                                    value={formData.nameContains || ''}
                                    onChange={(e) => setFormData({...formData, nameContains: e.target.value})}
                                    placeholder="e.g., Montrachet"
                                />
                            </div>
                            <div style={styles.row}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Drinking Window Min Year</label>
                                    <input
                                        type="number"
                                        style={styles.input}
                                        value={formData.drinkingWindowMinYear || ''}
                                        onChange={(e) => setFormData({...formData, drinkingWindowMinYear: e.target.value ? parseInt(e.target.value) : undefined})}
                                        placeholder="e.g., 2026"
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Accepted Maturities (comma-separated)</label>
                                    <input
                                        style={styles.input}
                                        value={formData.acceptedMaturities || ''}
                                        onChange={(e) => setFormData({...formData, acceptedMaturities: e.target.value})}
                                        placeholder="e.g., READY_YOUNG,READY_AT_BEST"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Trigger Conditions */}
                        <div style={styles.section}>
                            <h3 style={styles.sectionTitle}>Alert Triggers</h3>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={formData.alertOnNewListings || false}
                                        onChange={(e) => setFormData({...formData, alertOnNewListings: e.target.checked})}
                                    />
                                    Alert on new listings
                                </label>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={formData.alertOnPriceDrop || false}
                                        onChange={(e) => setFormData({...formData, alertOnPriceDrop: e.target.checked})}
                                    />
                                    Alert on price drops
                                </label>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>
                                    <input
                                        type="checkbox"
                                        style={styles.checkbox}
                                        checked={formData.alertOnRevaluation || false}
                                        onChange={(e) => setFormData({...formData, alertOnRevaluation: e.target.checked})}
                                    />
                                    Alert on revaluation
                                </label>
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
                            <button
                                style={{...styles.button, backgroundColor: 'var(--ink-500)'}}
                                onClick={handleCloseForm}
                            >
                                Cancel
                            </button>
                            <button
                                style={styles.button}
                                onClick={handleSave}
                                disabled={!formData.name}
                            >
                                {editingRule ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertRules;
